/**
 * Objects (Library Items) Data Source
 */

import type { 
  DataSourceModule, 
  FieldMetadata, 
  FieldType,
  ColumnDefinition, 
  ListWidgetItem,
  TableDataOutput,
  TableItemsOutput,
  TableDataRow,
  DataSourceConfig,
  TransformerContext
} from '../types';
import { getCachedLibraryItems, getCachedLibraryItemsByFolder, GlossaryItem } from "@/lib/stores/objectsStore";
import { getPendingChangeById } from "@/lib/stores/pendingChangesStore";
import { getFolderPath } from "@/lib/stores/folderStore";
import { formatDate, formatTags, formatVersion, formatSectionCount } from '../shared/formatters';
import { CardData } from '@/components/layout/element-layout/grid-layout/GridLayout';
import { formatRelativeTime, convertTagIdsToNames } from "@/lib/utils";
import { formatVersionForDisplay, compareVersions } from "@/lib/utils/versionUtils";
import { getStatusColor } from "@/lib/utils/statusColors";
import { resolveFieldDisplayValue, mapFieldTypeToLinkedDataSource } from "@/lib/utils/fieldValueResolver";

// Field definitions with metadata
const fields: Record<string, FieldMetadata> = {
  id: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  name: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
    required: true,
    validation: [
      { type: 'required', message: 'Name is required' }
    ]
  },
  type: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    linkedDataSource: 'types',
    displayField: 'name',
    valueField: 'id',
  },
  tags: {
    type: 'array',
    editable: 'dropdown',
    filterable: true,
    sortable: false,
    linkedDataSource: 'tags',
    multiSelect: true,
    displayField: 'name',
    valueField: 'id',
    formatter: formatTags
  },
  identifiers: {
    type: 'array',
    editable: 'text',
    filterable: true,
    sortable: false,
    formatter: (val) => Array.isArray(val) ? val.join(', ') : 'N/A'
  },
  blueprint_id: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
    linkedDataSource: 'templates',
    displayField: 'name',
    valueField: 'id',
  },
  blueprint_version: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
    formatter: formatVersion
  },
  version: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
    formatter: formatVersion
  },
  created_by: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
    linkedDataSource: 'users',
    displayField: 'username',
    valueField: 'id',
  },
  updated_by: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
    linkedDataSource: 'users',
    displayField: 'username',
    valueField: 'id',
  },
  folder: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    linkedDataSource: 'folders',
    folderItemType: 'library',
    displayField: 'name',
    valueField: 'id',
  },
  owner: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    linkedDataSource: 'users',
    displayField: 'username',
    valueField: 'id',
  },
  edit_permissions: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: false,
  },
  status: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    linkedDataSource: 'statuses',
  },
  created_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  },
  updated_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  }
};

// Column definitions
const columns: ColumnDefinition[] = [
  { 
    key: 'id', 
    label: 'ID', 
    filterable: true, 
    sortable: true,
    metadata: fields.id 
  },
  { 
    key: 'name', 
    label: 'Name', 
    filterable: true, 
    sortable: true,
    editable: 'text',
    metadata: fields.name 
  },
  { 
    key: 'type', 
    label: 'Type', 
    filterable: true, 
    sortable: true,
    metadata: fields.type 
  },
  { 
    key: 'tags', 
    label: 'Tags', 
    filterable: true,
    metadata: fields.tags 
  },
  { 
    key: 'identifiers', 
    label: 'Identifiers', 
    editable: 'text',
    metadata: fields.identifiers 
  },
  { 
    key: 'blueprint_id', 
    label: 'Blueprint ID', 
    filterable: true,
    metadata: fields.blueprint_id 
  },
  { 
    key: 'blueprint_version', 
    label: 'Blueprint Version', 
    filterable: true,
    metadata: fields.blueprint_version 
  },
  { 
    key: 'version', 
    label: 'Version', 
    filterable: true,
    metadata: fields.version 
  },
  { 
    key: 'created_by', 
    label: 'Created By', 
    filterable: true,
    metadata: fields.created_by 
  },
  { 
    key: 'updated_by', 
    label: 'Updated By', 
    filterable: true,
    metadata: fields.updated_by 
  },
  { 
    key: 'folder', 
    label: 'Folder', 
    filterable: true,
    metadata: fields.folder 
  },
  { 
    key: 'owner', 
    label: 'Owner', 
    filterable: true,
    metadata: fields.owner 
  },
  { 
    key: 'edit_permissions', 
    label: 'Edit Permissions', 
    filterable: true,
    metadata: fields.edit_permissions 
  },
  { 
    key: 'status', 
    label: 'Status', 
    filterable: true,
    editable: 'dropdown',
    metadata: fields.status 
  },
  { 
    key: 'created_at', 
    label: 'Created', 
    align: 'left',
    sortable: true,
    metadata: fields.created_at 
  },
  { 
    key: 'updated_at', 
    label: 'Updated', 
    align: 'left',
    sortable: true,
    metadata: fields.updated_at 
  }
];

// Configuration
const config: DataSourceConfig = {
  type: 'objects',
  title: 'Objects',
  description: 'Objects from the library',
  expandable: false,
  icon: 'box'
};

// Data fetcher - merges pending changes with cached data
function fetchData(selectedGame?: any, selectedFolderId?: string | null): GlossaryItem[] {
  if (!selectedGame?.id) return [];
  
  // Get base data from cache
  let baseData: GlossaryItem[];
  if (selectedFolderId !== undefined && selectedFolderId !== null) {
    baseData = getCachedLibraryItemsByFolder(selectedGame.id, selectedFolderId);
  } else {
    baseData = getCachedLibraryItems(selectedGame.id);
  }
  
  // Merge pending changes into the data
  // This ensures that local edits are immediately visible everywhere
  return baseData.map(item => {
    const pendingChange = getPendingChangeById(item.id);
    if (pendingChange && pendingChange.pendingData) {
      // Use pending data instead of cached data for items with local changes
      return {
        ...item,
        ...pendingChange.pendingData,
        // Ensure we keep the ID and game_id from the original
        id: item.id,
        game_id: item.game_id,
      };
    }
    return item;
  });
}

// Transformers
function toList(rawData: GlossaryItem[]): ListWidgetItem[] {
  return rawData.map(item => ({
    id: item.id,
    name: item.name,
    tags: formatTags(item.tags),
    type: item.type || 'Object',
    created_at: item.created_at,
    data: { sections: item.data || [], sectionsCount: item.data?.length || 0 },
    updated_at: item.updated_at,
    description: formatSectionCount(item.data)
  }));
}

function toTable(rawData: GlossaryItem[]): TableDataOutput {
  return {
    columns,
    data: rawData.map(item => ({
      id: item.id,
      name: String(item.name || ''),
      type: String(item.type || 'Object'),
      tags: Array.isArray(item.tags) ? item.tags : [],
      identifiers: Array.isArray(item.identifiers) ? item.identifiers : [],
      blueprint_id: item.blueprint_id || '',
      blueprint_version: item.blueprint_version ? formatVersion(item.blueprint_version) : '',
      version: item.version ? formatVersion(item.version) : '',
      folder: getFolderPath(item.game_id, 'library', item.folder) || '',
      owner: String(item.owner || ''),
      edit_permissions: String(item.edit_permissions || ''),
      status: String(item.status || ''),
      created_at: formatDate(item.created_at),
      updated_at: formatDate(item.updated_at),
    })),
    metadata: {
      totalCount: rawData.length,
      filteredCount: rawData.length
    }
  };
}

/**
 * Helper: Find description from item's section fields
 */
function findDescriptionFromData(data: any[]): string {
  if (!data || !Array.isArray(data)) return "No description available";
  
  const descriptionSection = data.find(section => 
    section?.fields?.some((field: any) => field?.label?.toLowerCase().includes('description'))
  );
  const descriptionField = descriptionSection?.fields.find((field: any) => 
    field?.label?.toLowerCase().includes('description')
  );
  
  return String(descriptionField?.value || "") || "No description available";
}

/**
 * Transform raw data to CardData format
 * @param rawData - Array of GlossaryItem from store
 * @param context - Optional context with tags, blueprints, getUserNameById for resolving IDs
 */
function toCard(rawData: GlossaryItem[], context?: TransformerContext): CardData[] {
  const { tags = [], blueprints = [], getUserNameById } = context || {};
  
  return rawData.map(item => {
    // Find the associated blueprint to get its color and check version mismatch
    const associatedBlueprint = item.blueprint_id && blueprints.length > 0
      ? blueprints.find((bp: any) => bp && bp.id === item.blueprint_id)
      : null;

    // Calculate version mismatch
    const hasVersionMismatch = item.blueprint_id &&
      item.blueprint_version &&
      associatedBlueprint?.version
        ? compareVersions(item.blueprint_version, associatedBlueprint.version) !== 0
        : false;

    // Format version string
    const versionString = item.version ? formatVersionForDisplay(item.version) : '0.000.001';
    
    // Get status color
    const statusColor = getStatusColor(item.status, 'objects', item.game_id);
    
    // Resolve owner name
    const ownerName = item.owner && getUserNameById 
      ? getUserNameById(item.owner) 
      : (item.owner || '');
    
    // Resolve tag names
    const tagNames = tags.length > 0 
      ? convertTagIdsToNames(item.tags, tags) 
      : (Array.isArray(item.tags) ? item.tags : []);

    const cardData = {
      id: item.id,
      gameId: item.game_id,
      title: item.name || '',
      owner: ownerName || '',
      ownerId: item.owner || undefined,
      editPermissions: item.edit_permissions || 'Open',
      creator: "System",
      tags: tagNames,
      description: findDescriptionFromData(item.data),
      lastUpdate: formatRelativeTime(item.updated_at),
      version: versionString,
      iconBg: associatedBlueprint?.settings?.color as string | undefined,
      shadowType: item.status ? "status" as const : "black" as const,
      shadowColor: statusColor,
      statusColor: statusColor,
      hasVersionMismatch: hasVersionMismatch,
      status: item.status,
      folder: item.folder,
      itemType: 'library',
      blueprint_id: item.blueprint_id || undefined,
      blueprintVersion: item.blueprint_version ? formatVersionForDisplay(item.blueprint_version) : undefined,
      type: item.type,
      identifiers: item.identifiers,
    };
    return cardData;
  });
}

/**
 * Column keys for library table view (matching the UI expectations)
 */
const TABLE_COLUMNS = [
  "Name", "Type", "Owner", "Tags", "Status", "Last Update", 
  "Folder", "Identifiers", "Blueprint", "Blueprint Version", "Version"
];

/**
 * Transform raw data to TableItem[] format for MainTableWidget
 * @param rawData - Array of GlossaryItem from store
 * @param context - Optional context with currentUserId, blueprints lookup
 */
function toTableItems(rawData: GlossaryItem[], context?: TransformerContext): TableItemsOutput {
  const { tags = [], blueprints = [], getUserNameById, currentUserId } = context || {};
  
  // Create blueprints map for name lookup
  const blueprintsMap = new Map<string, string>();
  blueprints.forEach((bp: any) => {
    if (bp && bp.id && bp.name) {
      blueprintsMap.set(bp.id, bp.name);
    }
  });
  
  // Build enhanced field metadata including blueprint custom fields
  const enhancedFieldMetadata: Record<string, FieldMetadata> = { ...fields };
  
  // Extract field metadata from blueprint data structures
  rawData.forEach(item => {
    if (item.data && Array.isArray(item.data)) {
      item.data.forEach((section: any) => {
        if (section.fields && Array.isArray(section.fields)) {
          section.fields.forEach((field: any) => {
            const fieldKey = field.id;
            const sectionKey = section.id ? `${section.id}_${fieldKey}` : fieldKey;
            
            // Only add if not already defined
            if (!enhancedFieldMetadata[sectionKey] && !enhancedFieldMetadata[fieldKey]) {
              const linkedDataSource = mapFieldTypeToLinkedDataSource(field.type);
              const isMultiple = field.multiple === true;
              
              console.log('[ObjectsDataSource] Creating field metadata:', {
                fieldId: field.id,
                fieldType: field.type,
                sectionKey,
                linkedDataSource,
                isMultiple,
              });
              
              enhancedFieldMetadata[sectionKey] = {
                type: getFieldMetadataType(field.type),
                editable: linkedDataSource ? 'dropdown' : (field.type === 'boolean' ? 'checkbox' : 'text'),
                filterable: true,
                sortable: false,
                linkedDataSource,
                multiSelect: isMultiple,
                displayField: 'name',
                valueField: 'id',
              };
            }
          });
        }
      });
    }
  });
  
  console.log('[ObjectsDataSource] Enhanced field metadata keys:', Object.keys(enhancedFieldMetadata));
  
  const items: TableDataRow[] = rawData.map(item => {
    // Determine lock status
    const ownerId = item.owner;
    const editPermissions = item.edit_permissions || 'Open';
    const isLocked = editPermissions === 'Locked' && 
                     ownerId !== undefined && 
                     currentUserId !== undefined && 
                     ownerId !== currentUserId;
    
    // Resolve owner name
    const ownerName = item.owner && getUserNameById 
      ? getUserNameById(item.owner) 
      : (item.owner || '');
    
    // Resolve tag names  
    const tagNames = tags.length > 0 
      ? convertTagIdsToNames(item.tags, tags) 
      : (Array.isArray(item.tags) ? item.tags : []);
    
    // Get blueprint name - handle both ID and name display
    const blueprintName = item.blueprint_id 
      ? (blueprintsMap.get(item.blueprint_id) || item.blueprint_id) 
      : "";
    
    return {
      type: "row" as const,
      key: item.id,
      depth: 0,
      isLocked,
      ownerId,
      editPermissions: editPermissions as 'Open' | 'Locked',
      content: TABLE_COLUMNS.map((columnKey, index) => {
        const normalizedKey = columnKey.toLowerCase().replace(/\s+/g, "");
        let content: string | boolean | number = "";

        switch (normalizedKey) {
          case "name":
            content = item.name || "";
            break;
          case "type":
            content = item.type || "";
            break;
          case "owner":
            content = ownerName || "";
            break;
          case "tags":
            content = tagNames.join(", ");
            break;
          case "status":
            content = item.status || "";
            break;
          case "lastupdate":
            content = formatRelativeTime(item.updated_at);
            break;
          case "folder":
            content = getFolderPath(item.game_id, 'library', item.folder) || "";
            break;
          case "identifiers":
            content = Array.isArray(item.identifiers) ? item.identifiers.join(", ") : "";
            break;
          case "blueprint":
            content = blueprintName || "";
            break;
          case "blueprintversion":
            content = item.blueprint_version ? formatVersionForDisplay(item.blueprint_version) : "";
            break;
          case "version":
            content = item.version ? formatVersionForDisplay(item.version) : "";
            break;
          default:
            content = "";
        }

        return {
          key: columnKey,
          fixedX: index === 0,
          content,
        };
      }),
    };
  });

  return {
    items,
    columns: TABLE_COLUMNS,
    fieldMetadata: enhancedFieldMetadata,
  };
}

/**
 * Helper to map field type string to FieldType for metadata
 */
function getFieldMetadataType(fieldType: string): FieldType {
  switch (fieldType) {
    case 'boolean':
      return 'boolean';
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'types':
    case 'glossaryItem':
    case 'glossary-item':
    case 'dialog':
      // These are reference fields, technically strings (UUIDs)
      return 'string';
    default:
      return 'string';
  }
}

// Export the module
export const objectsDataSource: DataSourceModule = {
  type: 'objects',
  config,
  fields,
  columns,
  fetchData,
  transformers: {
    toList,
    toTable,
    toCard,
    toTableItems
  }
};

// Export table columns for use in components
export { TABLE_COLUMNS as OBJECTS_TABLE_COLUMNS };

